import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { MenuItem, Grid, List, ListItem, TextField, Typography } from '@material-ui/core';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import Pagination from '@material-ui/lab/Pagination';
import { toast } from 'react-toastify';
import useDebounce from '../global/use-debounce';
import OverLoading from '../global/OverLoading';
import Constants, { Status } from '../../common/constants';
import { IsEmpty } from '../../common/checks';
import apiService from '../../services.js/apiService';
import FreeAd from './item';
import SelectPageModal from './selectPageModal';
import PostTypeModal from '../post/postTypeModal';

const FreeAdsList = () => {
  const [loading, setLoading] = useState(false);
  const [freeAds, setFreeAds] = useState([]);

  /* filters */
  const [filterStatus, setFilterStatus] = useState('Pending');
  const [filterPageName, setFilterPageName] = useState('');
  const [filterCreatedAt, setFilterCreatedAt] = useState('ASC');

  const [freeAdsCount, setFreeAdsCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageNo, setPageNo] = useState(1);

  const [freeAdIdSelected, setFreeAdIdSelected] = useState(null);
  const [showSelectPageModal, setShowSelectPageModal] = useState(false);
  const [showSelectPostTypeModal, setShowSelectPostTypeModal] = useState(false);

  const debouncedSearch = useDebounce(filterPageName, 500);

  // This is the displayed paginator value change (starting from value 1)
  const handleChange = (event, value) => setPageNo(value);

  async function fetchFreeAdsData(status, pageName, createdAt, pageSize, pageNo) {
    setLoading(true);
    pageNo -= 1; // default UI pagination is from value 1, but we need to send to backend as 0 for the first page
    await apiService.getFreeAds({ status, pageName, createdAt, pageSize, pageNo })
      .then((result) => {
        if (result) {
          setLoading(false);
          setFreeAdsCount(result.totalCount);
          setFreeAds(result.data);

          // Check if whole number. returns true otherwise false.
          if (Number.isInteger(result.totalCount / pageSize)) {
            setTotalCount(Math.floor(result.totalCount / pageSize));
          } else {
            // we add +1 from the total count for the remainder.
            setTotalCount(Math.floor(result.totalCount / pageSize) + 1);
          }
        }
      })
      .catch((err) => { toast.error(err); setLoading(false); });
  }

  useEffect(() => {
    fetchFreeAdsData(filterStatus, filterPageName, filterCreatedAt, pageSize, pageNo);
  }, [filterStatus, filterCreatedAt, pageSize, pageNo]);

  useEffect(() => {
    fetchFreeAdsData(filterStatus, filterPageName, filterCreatedAt, pageSize, pageNo);
  }, []);

  useEffect(() => {
    fetchFreeAdsData(filterStatus, filterPageName, filterCreatedAt, pageSize, pageNo);
  }, [debouncedSearch]);

  const updateStatusInListWithoutRemovingIt = (id, newStatus) => {
    const freeAdsCopy = freeAds.slice();

    for (const freeAd of freeAdsCopy) {
      if (freeAd.id === id) {
        freeAd.status = newStatus;
        break;
      }
    }

    setFreeAds(freeAdsCopy);
    fetchFreeAdsData(filterStatus, filterPageName, filterCreatedAt, pageSize, pageNo);
  };

  const removeAdFromList = (id) => {
    const ads = _.filter(freeAds, (o) => o.id !== id);
    setFreeAds(ads);

    if (ads.length === 0) {
      fetchFreeAdsData(filterStatus, filterPageName, filterCreatedAt, pageSize, pageNo);
    }
  };

  const onAcceptClicked = (id) => {
    setFreeAdIdSelected(id);
    setShowSelectPageModal(true);
  };

  const onRejectClicked = (id) => {
    apiService.updateFreeAd(id, { status: Status.Rejected })
      .then(() => {
        // so user can continue to process
        if (filterStatus === Status.Pending) {
          removeAdFromList(id);
        } else {
          // Else just update the list
          updateStatusInListWithoutRemovingIt(id, Status.Rejected);
        }
        toast.success('Ad rejected');
      })
      .catch((err) => toast.error(err));
  };

  const onRevertClicked = (id) => {
    apiService.updateFreeAd(id, { status: Status.Pending })
      .then(() => {
        // Keep the post shown in the listing so that user can re-process it again
        updateStatusInListWithoutRemovingIt(id, Status.Pending);
        toast.success('Ad reverted');
      })
      .catch((err) => toast.error(err));
  };

  const updateFreeAdToPageId = async (freeAdId, pageId) => {
    try {
      await apiService.updateFreeAd(freeAdId, { pageId });
      setShowSelectPageModal(false);
      setShowSelectPostTypeModal(true);
    } catch (err) {
      toast.error(err);
    }
  };

  const convertFreeAdToPost = async (freeAdId, postType, promoType) => {
    try {
      const post = await apiService.convertFreeAdToPost(freeAdId, { postType, promoType });
      setShowSelectPostTypeModal(false);

      if (filterStatus === Status.Pending) {
        removeAdFromList(freeAdId);
      } else {
        // Else just update the list
        updateStatusInListWithoutRemovingIt(freeAdId, Status.Accepted);
      }
      window.open(`/admin/page/${post.pageId}/post/${post.id}`, '_blank');
    } catch (err) {
      toast.error(String(err));
    }
  };

  return (
    <>
      <div className="row" style={{ justifyContent: 'center' }}>
        <div className="col-md-12">
          <div className="content-header">
            <Grid item container direction="row" md={12} xs={12} style={{ minHeight: 60 }}>
              <Grid item xs={12} md={12}>
                <Typography variant="h1">Free Ads</Typography>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>

      <div className="row" style={{ justifyContent: 'center' }}>
        <div className="col-md-12">
          <div className="content-header">
            <Grid item container direction="row" md={12} xs={12} spacing={1} style={{ minHeight: 60 }}>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Status"
                  value={filterStatus}
                  style={{ position: 'relative', width: '100%', paddingBottom: 10, marginTop: 10 }}
                  select
                  onChange={(e) => {
                    setPageNo(1);
                    setFilterStatus(e.target.value);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={Constants.Status.Pending}>{ Constants.Status.Pending }</MenuItem>
                  <MenuItem value={Constants.Status.Rejected}>{ Constants.Status.Rejected }</MenuItem>
                  <MenuItem value={Constants.Status.Accepted}>{Constants.Status.Accepted}</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Search by Shop name or Page name"
                  style={{ position: 'relative', width: '100%', paddingBottom: 10, marginTop: 10 }}
                  onChange={(e) => {
                    setPageNo(1);
                    setFilterPageName(e.target.value);
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Sort by submitted date"
                  value={filterCreatedAt}
                  style={{ position: 'relative', width: '100%', paddingBottom: 10, marginTop: 10 }}
                  select
                  onChange={(e) => {
                    setPageNo(1);
                    setFilterCreatedAt(e.target.value);
                  }}
                >
                  <MenuItem value="ASC">
                    Earliest
                    {' '}
                    <ArrowRightAltIcon />
                    {' '}
                    Latest
                  </MenuItem>
                  <MenuItem value="DESC">
                    Latest
                    {' '}
                    <ArrowRightAltIcon />
                    {' '}
                    Earliest
                  </MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </div>
        </div>
        <div className="col-md-12">
          {loading && <OverLoading />}
          { IsEmpty(freeAds) && (
            <div className="alert alert-primary text-center mt-1 p-4" role="alert">
              <b>No Ad(s) found.</b>
            </div>
          ) }
          <List>
            { freeAds.map((d) => (
              <ListItem key={d.id}>
                <FreeAd
                  key={d.id}
                  id={d.id}
                  title={d.title}
                  isTakeaway={d.isTakeaway}
                  isDelivery={d.isDelivery}
                  orderInfo={d.orderInfo}
                  photo={d.photo}
                  address={d.address}
                  pageName={d.pageName}
                  contact={d.contact}
                  terms={d.terms}
                  status={d.status}
                  lastUpdatedBy={d.lastUpdatedBy}
                  createdAt={d.createdAt}
                  onRejectClicked={() => onRejectClicked(d.id)}
                  onAcceptClicked={() => onAcceptClicked(d.id)}
                  onRevertClicked={() => onRevertClicked(d.id)}
                />
              </ListItem>
            )) }
          </List>

          <div style={{
            position:   'fixed',
            bottom:     0,
            width:      '100%',
            background: '#fff',
            padding:    '30px 10px',
            zIndex:     9,
          }}
          >
            <>
              {totalCount > 0 && (
                <Pagination
                  color="secondary"
                  size="large"
                  count={totalCount}
                  page={pageNo}
                  onChange={handleChange}
                  className="float-left"
                />
              )}
              <p style={{
                display:  'inline-block',
                margin:   '10px 0 0 30px',
                fontSize: 17,
              }}
              >
                Total count:
                {' '}
                {freeAdsCount}
              </p>
            </>
          </div>
        </div>
      </div>

      <SelectPageModal
        open={showSelectPageModal}
        onCancel={() => setShowSelectPageModal(false)}
        onSelect={(pageId) => {
          updateFreeAdToPageId(freeAdIdSelected, pageId);
        }}
      />

      {
        showSelectPostTypeModal
        && (
          <PostTypeModal
            onClose={() => setShowSelectPostTypeModal(false)}
            onComplete={({ postType, promoType }) => {
              convertFreeAdToPost(freeAdIdSelected, postType, promoType);
            }}
          />
        )
      }
    </>
  );
};

FreeAdsList.postTypes = {
};

export default connect(null, null)(FreeAdsList);
